import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { WonderPush } from '@awesome-cordova-plugins/wonderpush/ngx';
import { Platform } from '@ionic/angular';
import { httpsCallable, getFunctions } from 'firebase/functions';
import mixpanel from 'mixpanel-browser';
import { filter, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { countries } from '../../auth/sign-up/countries';
import { ProfileStore } from '../store/profile-store';
import { AuthService } from '../../firebase/auth.service';
import { confirmForDelete, confirmModalData } from '../profile.service';
import { IUser } from 'src/app/auth/auth.models';
import { FormService } from 'src/app/shared/components/form/form.service';
import { environment } from '../../../environments/environment';
import { PoolsStore } from 'src/app/merfolk/pools/pools.store';
import { PodStore } from 'src/app/merfolk/pods/pod-store';
import { SecondHandStore } from 'src/app/bazaar/second-hand/second-hand-store';
import { JobsStore } from 'src/app/bazaar/jobs-services/jobs/jobs-store';
import { ServicesStore } from 'src/app/bazaar/jobs-services/services/services.store';
import {
  createToaster,
  positionType,
} from 'src/app/shared/components/modals/toaster';
import { showDeleteLoading } from './../../shared/components/modals/loading';
import { MapService } from 'src/app/services/map.service';
import { PaymentsStore } from '../../payments/payments-store';
import { UserService } from '../../auth/user.service';
import { subscriptionMsg } from '../../payments/payments';
import { PushNotificationsService } from 'src/app/services/push-notifications.service';
import { TranslationService } from 'src/app/services/translation.service';

@Component({
  selector: 'app-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
})
export class EditProfileComponent
  implements OnInit, OnDestroy, AfterContentChecked
{
  form: FormGroup;
  countries = countries;
  userId = this.activatedRouter.snapshot.params.id;
  currentUser: IUser;

  updateLoading = false;
  deleteProfileLoading = false;

  isFormSubmitting: string | boolean = 'loaded';
  loader = showDeleteLoading();

  showConfirmModal = false;
  confirmModalData = confirmModalData;
  confirmForDelete = confirmForDelete;
  showPasswordForm = false;
  showForDelete = false;
  loading = false;
  isValidLink = true;
  today = new Date().toISOString();

  userSubscriptionInfo;
  userSubInfo;
  isUserPremium;
  isAppPremium;
  isPersonalPremium;
  isPremium;
  isAdmin;
  decodedToken;
  balanceInfo;
  user;
  authUserId;

  subscriptionMsgAppPremium = subscriptionMsg.APP_PREMIUM;
  subscriptionMsgPersonalPremium = subscriptionMsg.PERSONAL_PREMIUM;

  isMobile = this.platform.platforms().includes('capacitor');
  isIosApp = this.platform.platforms().includes('ios') && this.isMobile;
  private destroy$ = new Subject<void>();

  readonly user$ = this.profileStore.selectItem();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private fb: FormBuilder,
    private profileStore: ProfileStore,
    private authService: AuthService,
    private activatedRouter: ActivatedRoute,
    private formService: FormService,
    private poolsStore: PoolsStore,
    private podsStore: PodStore,
    private secondHandStore: SecondHandStore,
    private servicesStore: ServicesStore,
    private jobsStore: JobsStore,
    private router: Router,
    private platform: Platform,
    private mapService: MapService,
    private paymentsStore: PaymentsStore,
    private userService: UserService,
    private fireStore: AngularFirestore,
    private wonderPush: WonderPush,
    private notificationsService: PushNotificationsService,
    private translationService: TranslationService
  ) {}

  ngOnInit() {
    const authUser = this.authService.getCurrentUser();
    // Check if user is trying to access someone else's profile
    if (authUser && authUser.uid !== this.userId) {
      // Redirect to their own profile edit page
      this.router.navigate(['/profile/edit', authUser.uid]);
      return;
    }
    this.createForm();
    this.profileStore.getItemByByValue({ uid: authUser.uid });
    this.getUser();
    this.listenUserStoreChanges();
    this.listenStatusChanges();

    // Update auto-translate handler
    this.form
      .get('autoTranslate')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(async value => {
        // Use the new method to handle translation state
        await this.translationService.setAutoTranslate(value);

        // Update user profile
        if (this.currentUser) {
          const updatedUser = {
            ...this.currentUser,
            autoTranslate: value,
          };
          this.profileStore.updateProfile(updatedUser);
        }
      });

    this.form
      .get('pushNotificationsEnabled')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => this.updatePushNotificationPreference(!!value));
  }

  async addMapLink(e) {
    e.valueChanges.subscribe(async () => {
      this.isValidLink = true;
      const location = e.get('googleMapLink')?.value;
      const { country } = await this.mapService.getAddress(location);
      if (!country) {
        this.isValidLink = false;
      }

      this.form.get('googleMapLink')?.patchValue(location);
      this.form.patchValue({ country });
      this.form.markAsDirty();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  OpenDeleteModal() {
    this.showForDelete = true;
  }

  async openSubscriptionRef() {
    this.loading = true;
    const functions = getFunctions();
    const openSub = httpsCallable(
      functions,
      'ext-firestore-stripe-payments-createPortalLink'
    );
    const { data } = await openSub({
      returnUrl: environment.hostUrl,
    });

    window.open((data as any).url, '_blank');
    this.loading = false;
  }

  updateUser() {
    const {
      galleryVisibility,
      autoTranslate,
      gender,
      birthday,
      mermaidExpLvl,
      mersonaName,
      bio,
      isMailingEnabled,
    } = this.form.value;

    // Convert birthday to ISO string if it exists to ensure proper date format
    const formattedBirthday = birthday
      ? new Date(birthday).toISOString()
      : null;

    const newUser = {
      ...this.currentUser,
      galleryVisibility,
      autoTranslate,
      gender,
      birthday: formattedBirthday,
      mermaidExpLvl,
      mersonaName,
      bio,
      isMailingEnabled: isMailingEnabled || false,
    };

    // Update profile which will trigger translation service changes
    this.profileStore.updateProfile(newUser);
  }

  signOut() {
    this.profileStore.logOut();
    this.poolsStore.logOut();
    this.authService.signOut();
    this.podsStore.logOut();
    this.servicesStore.logOut();
    this.jobsStore.logOut();
    this.secondHandStore.logOut();
    this.formService.location$.next({ googleMapLink: null });
    this.notificationsService.unsubscribe();
    this.wonderPush.unsubscribeFromNotifications();
    mixpanel.init(environment.mixpanelToken, { debug: true });
    mixpanel.identify(this.user?.uid);
    mixpanel.people.set({
      'Notification Settings Changes': 'disabled',
    });
    this.wonderPush.setUserId(null);
  }

  discardChanges(e) {
    if (e) {
      this.showPasswordForm = false;
      this.showConfirmModal = false;
      this.form.reset();
    }
  }

  closeModal(e) {
    if (e) {
      this.showConfirmModal = false;
    }
  }

  openConfirmModal() {
    this.showConfirmModal = true;
  }

  toggleUpdatePasswordModal(e) {
    this.showPasswordForm = e;
  }

  private listenStatusChanges() {
    this.profileStore
      .selectLoading()
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        loading
          ? (this.isFormSubmitting = true)
          : (this.isFormSubmitting = false);
      });
  }

  private createForm() {
    this.form = this.fb.group({
      galleryVisibility: [null],
      autoTranslate: [true],
      country: ['', Validators.required],
      googleMapLink: ['', [Validators.required]],
      profilePicture: [''],
      birthday: [''],
      gender: [''],
      mermaidExpLvl: [''],
      mersonaName: [''],
      bio: [''],
      isMailingEnabled: [false],
      pushNotificationsEnabled: [false],
    });
  }

  async deleteProfile(_e) {
    if (_e) {
      this.deleteProfileLoading = true;
      this.closeDeleteModal(true);
      const data = await this.loader;
      data.present();

      try {
        const isDelete = await this.authService.deleteProfile();
        if (isDelete) {
          this.removeNotificationUserData();
          mixpanel.init(environment.mixpanelToken, { debug: true });
          mixpanel.identify(this.currentUser?.uid);
          mixpanel.people.set({
            $deletedAt: new Date(),
          });
          this.wonderPush.unsubscribeFromNotifications();
          this.signOut();
          const data = await this.loader;
          data.dismiss();
          this.deleteProfileLoading = false;
          createToaster('successfully deleted', 'success', positionType.BOTTOM);
          this.router.navigate(['/auth/login']);
        }
      } catch (e) {
        createToaster(
          'something went wrong, please, try again',
          'danger',
          positionType.BOTTOM
        );
        this.deleteProfileLoading = false;
        console.log(e);
      }
    }
  }

  closeDeleteModal(_e) {
    if (_e) this.showForDelete = false;
  }

  private listenUserStoreChanges() {
    this.user$
      .pipe(
        filter(user => !!user?.id),
        takeUntil(this.destroy$)
      )
      .subscribe(async (user: any) => {
        const location = this.mapService.normalizeLatLng(user.location);
        this.formService.location$.next({
          googleMapLink: location
            ? `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`
            : null,
        });
        this.currentUser = user;
        this.user = user;
        this.loading = false;
        const {
          galleryVisibility,
          autoTranslate,
          country,
          gender,
          birthday,
          mermaidExpLvl,
          mersonaName,
          bio,
          isMailingEnabled,
          notificationSettings,
        } = user;
        this.form.patchValue(
          {
            galleryVisibility,
            autoTranslate: autoTranslate === undefined ? true : autoTranslate,
            country: country.name,
            gender,
            birthday: birthday || '',
            mermaidExpLvl,
            mersonaName,
            bio: bio || '',
            isMailingEnabled: isMailingEnabled || false,
            pushNotificationsEnabled:
              !!notificationSettings?.wonderPushSubscribed,
          },
          { emitEvent: false }
        );

        // Ensure autoTranslate setting is properly initialized from user profile
        const autoTranslateValue =
          autoTranslate === undefined ? true : autoTranslate;
        localStorage.setItem('autoTranslate', autoTranslateValue.toString());
        await this.translationService.checkAutoTranslateSetting();
      });
  }

  private getUser() {
    this.loading = true;
    this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user.uid)
      )
      .subscribe(user => {
        this.user = user;
        this.authUserId = user.id;
        this.paymentsStore.getItemByByValue({ documentId: user.uid });
        this.isUserPremium = this.userService.isPaidPremium;
        this.isAdmin = this.userService.isAdmin;
        this.balanceInfo = this.userService.balanceInfo;
        this.isAppPremium = this.userService.isAppPremium;
        this.isPersonalPremium = this.userService.isPersonalPremium;
        this.isPremium = this.userService.isPremium;

        this.getSubscriptionInfo();
        this.loading = false;
      });
  }

  async getSubscriptionInfo() {
    this.userSubInfo = await this.fireStore
      .collection('users')
      .doc(this.authUserId)
      .collection('subscriptions')
      .ref.where('status', 'in', ['trialing', 'active'])
      .where('role', '==', 'premium');

    this.userSubInfo.onSnapshot(async snapshot => {
      if (this.isUserPremium) {
        const doc = snapshot.docs[0];
        this.userSubscriptionInfo = doc?.data();
      } else {
        this.userSubscriptionInfo = false;
      }
    });
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }
  get country() {
    return this.form.get('country');
  }
  get postalCode() {
    return this.form.get('postalCode');
  }
  get city() {
    return this.form.get('city');
  }
  get birthday() {
    return this.form.get('birthday');
  }
  get gender() {
    return this.form.get('gender');
  }
  get mermaidExpLvl() {
    return this.form.get('mermaidExpLvl');
  }
  get mersonaName() {
    return this.form.get('mersonaName');
  }
  get bio() {
    return this.form.get('bio');
  }
  get isMailingEnabled() {
    return this.form.get('isMailingEnabled');
  }
  get pushNotificationsEnabled() {
    return this.form.get('pushNotificationsEnabled');
  }
  get autoTranslate() {
    return this.form.get('autoTranslate');
  }

  removeNotificationUserData() {
    if (this.platform.is('capacitor')) {
      this.wonderPush.clearAllData();
    } else {
      this.notificationsService.removeUserData();
    }
  }

  private async updatePushNotificationPreference(enabled: boolean) {
    if (!this.currentUser?.uid) {
      return;
    }

    const result = await this.notificationsService.setPushNotificationsEnabled(
      this.currentUser.uid,
      enabled,
      this.wonderPush
    );

    if (enabled && !result) {
      this.pushNotificationsEnabled.patchValue(false, { emitEvent: false });
    }
  }
}

